import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Tooltip from "@/v2-ui/tooltip"
import Table, {
  createColumns,
  GROUPING_RANGE_DATES,
  type ColumnFilterOption,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { DotText } from "@/v2-backoffice-client/components/dot"
import { FENERUM_ACCOUNT_BASE_URL } from "@/v2-backoffice-client/constants"
import LicenseAccountTableRowToolbar from "@/v2-backoffice-client/license/account/LicenseAccountTableRowToolbar"
import { useLicenseAccountsQuery } from "@/v2-backoffice-client/license/account/license.account.query"
import {
  ACKNOWLEDGED_STATUS,
  ARCHIVED_STATUS,
  getLinkStatus,
  LINKED_STATUS,
  UNMATCHED_STATUS,
  type LicenseAccountLinkStatus,
} from "@/v2-backoffice-client/license/account/license.account.utils"
import type { LicenseAccountFragment } from "@/v2-backoffice-client/license/account/__types__/license.account.fragment"

// The link-status bucket, derived per row so the pills can filter on it —
// the classification itself lives in license.account.utils, shared with the
// Licensing page's attention chip.
type LicenseAccountRow = LicenseAccountFragment & {
  LinkStatus: LicenseAccountLinkStatus
}

type LinkStatusDisplay = {
  variant: "success-1" | "destructive" | "warning" | "muted"
  /** What the state means — the hover explanation. */
  description: string
  /** What an operator does about it; empty when nothing needs doing. */
  action?: string
}

// One entry per link state: color AND meaning, so the value explains itself
// on hover — the same treatment the Fenerum-status and payment-health badges
// get.
const LINK_STATUS_DISPLAY: Record<LicenseAccountLinkStatus, LinkStatusDisplay> =
  {
    [LINKED_STATUS]: {
      variant: "success-1",
      description:
        "Linked to its customer — subscriptions and invoices flow into the customer panel's Billing tab, and licenses derive from here.",
    },
    [UNMATCHED_STATUS]: {
      variant: "destructive",
      description:
        "No customer link, so nothing derives from this account — its subscriptions bill, but no license or revenue shows on any customer.",
      action:
        "The Reason column names the data fix (usually the org number). Fix the data and the hourly mirror links it on its own — or open the panel's Customers tab to link (or acknowledge) it by hand.",
    },
    [ACKNOWLEDGED_STATUS]: {
      variant: "warning",
      description:
        "Unmatched by design, and an operator has said why — intentional group billing, a duplicate account being consolidated. Off the needs-review worklist.",
      action:
        "Nothing, unless the situation changed: the note (hover the Acknowledged column, or open the panel) says what was decided. The mirror still links the row automatically if the ambiguity ever resolves.",
    },
    [ARCHIVED_STATUS]: {
      variant: "muted",
      description:
        "Fenerum archived this account — nothing is billed on it. Expected for churned customers; an existing link keeps mirroring history.",
      action:
        "If this customer is billed again, reactivate or recreate the account in Fenerum.",
    },
  }

function createLicenseAccountColumns(input: {
  reasonOptions: ColumnFilterOption[]
  sellingEntityOptions: ColumnFilterOption[]
  countryOptions: ColumnFilterOption[]
  languageOptions: ColumnFilterOption[]
}) {
  const {
    reasonOptions,
    sellingEntityOptions,
    countryOptions,
    languageOptions,
  } = input

  return createColumns<LicenseAccountRow>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      CompanyName: {
        id: "CompanyName",
        label: "Fenerum Account",
        description:
          "Opens the account panel — invoices, the customer link, and the Fenerum deep link.",
        defaultSize: 200,
        to: ({ row }) => `?v=billingAccount:${row.original.Id}`,
      },
      LegalVatNumber: {
        id: "LegalVatNumber",
        label: "Org Number",
        description:
          "Fenerum's legal_vat_number — the only key the customer link can be made on.",
        defaultSize: 110,
        filter: { type: "text", placeholder: "Org number..." },
        getValue: (row) => row.LegalVatNumber ?? "",
        renderCell({ row }) {
          const value = row.original.LegalVatNumber
          if (!value) return <DotText variant="destructive">Missing</DotText>
          return value
        },
        exportValue({ row }) {
          return row.original.LegalVatNumber ?? ""
        },
      },
      LinkStatus: {
        id: "LinkStatus",
        label: "Link",
        defaultSize: 110,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any link state",
          options: [
            {
              label: LINKED_STATUS,
              value: LINKED_STATUS,
              dotVariant: "success-1",
            },
            {
              label: UNMATCHED_STATUS,
              value: UNMATCHED_STATUS,
              dotVariant: "destructive",
            },
            {
              label: ACKNOWLEDGED_STATUS,
              value: ACKNOWLEDGED_STATUS,
              dotVariant: "warning",
            },
            { label: ARCHIVED_STATUS, value: ARCHIVED_STATUS },
          ],
        },
        renderCell({ row }) {
          const status = row.original.LinkStatus
          const display = LINK_STATUS_DISPLAY[status]
          return (
            <Tooltip>
              <Tooltip.Trigger asChild>
                <DotText variant={display.variant} className="cursor-help">
                  {status}
                </DotText>
              </Tooltip.Trigger>
              <Tooltip.Content side="top" className="ui-tooltip-content--panel">
                <div className="flex max-w-xs flex-col gap-1 p-3 text-xs">
                  <p className="font-medium">{status}</p>
                  <p className="text-muted-foreground">{display.description}</p>
                  {display.action && (
                    <p className="mt-1 text-muted-foreground">
                      {display.action}
                    </p>
                  )}
                </div>
              </Tooltip.Content>
            </Tooltip>
          )
        },
      },
      Customer: {
        id: "Customer",
        label: "Customer",
        defaultSize: 180,
        filter: { type: "text", placeholder: "Customer name..." },
        getValue: (row) => row.CustomerName ?? "",
        renderCell({ row }) {
          return row.original.CustomerName ?? null
        },
        to: ({ row }) => {
          const externalId = row.original.ExternalCustomerId
          if (!externalId) return
          return `>>?v=customer:${externalId}`
        },
      },
      UnmatchedReason: {
        id: "UnmatchedReason",
        label: "Reason",
        description:
          "Why the sync couldn't link this account. Every reason maps to a data fix — the hourly mirror heals the row once the data is corrected. Group on it to work the fixes in batches.",
        defaultSize: 260,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any reason",
          toolbar: true,
          options: reasonOptions,
        },
        getValue: (row) => row.UnmatchedReason ?? "",
        renderCell({ row }) {
          return row.original.UnmatchedReason ?? null
        },
      },
      MatchedCustomer: {
        id: "MatchedCustomer",
        label: "Org Number Match",
        description:
          "Who the account's org number points at today — the link the sync WOULD make, or the duplicate set blocking it.",
        defaultSize: 160,
        getValue: (row) =>
          row.MatchedCustomerCount > 1
            ? `${row.MatchedCustomerCount} customers`
            : (row.MatchedCustomerName ?? ""),
        renderCell({ row }) {
          const { LinkStatus, MatchedCustomerCount, MatchedCustomerName } =
            row.original
          if (LinkStatus !== UNMATCHED_STATUS) return null
          if (MatchedCustomerCount > 1) {
            return `${MatchedCustomerCount} customers`
          }
          return MatchedCustomerName ?? null
        },
      },
      ReviewedBy: {
        id: "ReviewedBy",
        label: "Acknowledged",
        description:
          "Who acknowledged this unmatched row, with the note saying why it is fine — intentional group billing, a duplicate account being consolidated. Set from the panel's Customers tab.",
        defaultSize: 140,
        defaultHidden: true,
        enableGrouping: true,
        filter: { type: "text", placeholder: "Acknowledged by..." },
        getValue: (row) => row.ReviewedByUserName ?? "",
        renderCell({ row }) {
          const { ReviewedByUserName, ReviewedAt, ReviewNote } = row.original
          if (!ReviewedAt) return null
          return (
            <Tooltip>
              <Tooltip.Trigger asChild>
                <span className="cursor-help">
                  {ReviewedByUserName ?? "Acknowledged"}
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content side="top" className="ui-tooltip-content--panel">
                <div className="flex max-w-xs flex-col gap-1 p-3 text-xs">
                  <p className="font-medium">
                    Acknowledged {formatDateString(ReviewedAt)}
                  </p>
                  <p className="text-muted-foreground">
                    {ReviewNote ?? "No note was left."}
                  </p>
                </div>
              </Tooltip.Content>
            </Tooltip>
          )
        },
        exportValue({ row }) {
          const { ReviewedByUserName, ReviewNote } = row.original
          if (!ReviewedByUserName && !ReviewNote) return ""
          return [ReviewedByUserName, ReviewNote].filter(Boolean).join(": ")
        },
      },
      LinkedBy: {
        id: "LinkedBy",
        label: "Linked By",
        description:
          "How the link was made: an operator from the panel's Customers tab, or empty for the mirror's own org-number match.",
        defaultSize: 120,
        defaultHidden: true,
        enableGrouping: true,
        filter: { type: "text", placeholder: "Linked by..." },
        getValue: (row) => row.LinkedByUserName ?? "",
      },
      SellingEntity: {
        id: "SellingEntity",
        label: "Selling Entity",
        defaultSize: 130,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any selling entity",
          toolbar: true,
          options: sellingEntityOptions,
        },
        getValue: (row) => row.SellingEntity ?? "",
      },
      IsEanInvoicing: {
        id: "IsEanInvoicing",
        label: "EAN Invoicing",
        defaultSize: 100,
        defaultHidden: true,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any invoicing",
          options: [
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ],
        },
        getValue: (row) => (row.IsEanInvoicing ? "Yes" : "No"),
        renderCell({ row }) {
          return row.original.IsEanInvoicing ? "Yes" : "No"
        },
      },
      LegalCountry: {
        id: "LegalCountry",
        label: "Country",
        defaultSize: 70,
        defaultHidden: true,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any country",
          options: countryOptions,
        },
        getValue: (row) => row.LegalCountry ?? "",
      },
      LanguageCode: {
        id: "LanguageCode",
        label: "Language",
        defaultSize: 80,
        defaultHidden: true,
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any language",
          options: languageOptions,
        },
        getValue: (row) => row.LanguageCode ?? "",
      },
      ErpId: {
        id: "ErpId",
        label: "ERP Id",
        defaultSize: 100,
        defaultHidden: true,
        filter: { type: "text", placeholder: "ERP id..." },
        getValue: (row) => row.ErpId ?? "",
      },
      ExternalAccountCode: {
        id: "ExternalAccountCode",
        label: "Fenerum Code",
        defaultSize: 110,
        defaultHidden: true,
        filter: { type: "text", placeholder: "Fenerum code..." },
        getValue: (row) => row.ExternalAccountCode ?? "",
      },
      ExternalLastModifiedAt: {
        id: "ExternalLastModifiedAt",
        label: "Changed in Fenerum",
        defaultSize: 110,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.ExternalLastModifiedAt
          return value ? formatDateString(value) : null
        },
        exportValue({ row }) {
          return formatDateString(row.original.ExternalLastModifiedAt)
        },
      },
      SyncedAt: {
        id: "SyncedAt",
        label: "Synced",
        defaultSize: 90,
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          const value = row.original.SyncedAt
          return value ? formatDateString(value) : null
        },
      },
      ExternalAccountUuid: {
        id: "ExternalAccountUuid",
        label: "Fenerum Uuid",
        defaultSize: 250,
        defaultHidden: true,
        to: ({ value }) => {
          if (!value) return
          return `${FENERUM_ACCOUNT_BASE_URL}/${value}/`
        },
      },
      Id: { id: "Id", label: "Id", defaultSize: 50, defaultHidden: true },
    },
  })
}

export type LicenseAccountTableProps = Omit<
  TableProps<LicenseAccountRow>,
  "data" | "columns"
>

// The Billing Accounts board: every Fenerum account the mirror stores, with
// the customer link when one was made and the reason when one wasn't.
// Doubles as the link-fixing worklist — the proc sorts unmatched rows first,
// the Unmatched pill isolates them, and each reason names the data fix that
// makes the hourly mirror heal the row on its own. Owns the accounts query
// (shared via the Apollo cache with the tab trigger's chip).
function LicenseAccountTable(props: LicenseAccountTableProps) {
  const { className, ...tableProps } = props

  const { data, loading, error, refetch } = useLicenseAccountsQuery()
  const accounts = data?.getBillingAccounts ?? []

  const rows = useMemo<LicenseAccountRow[]>(
    () =>
      accounts.map((account) => ({
        ...account,
        LinkStatus: getLinkStatus(account),
      })),
    [accounts],
  )

  const unmatchedCount = useMemo(
    () => rows.filter((row) => row.LinkStatus === UNMATCHED_STATUS).length,
    [rows],
  )
  const acknowledgedCount = useMemo(
    () => rows.filter((row) => row.LinkStatus === ACKNOWLEDGED_STATUS).length,
    [rows],
  )
  const linkedCount = useMemo(
    () => rows.filter((row) => row.LinkStatus === LINKED_STATUS).length,
    [rows],
  )

  // Options for the categorical filters come from the rows themselves: the
  // reason set is owned by the proc, entities/countries by Fenerum — deriving
  // keeps the filters honest about what exists without hardcoding either.
  const derivedOptions = useMemo(() => {
    const reasons = new Set<string>()
    const entities = new Set<string>()
    const countries = new Set<string>()
    const languages = new Set<string>()
    for (const account of accounts) {
      if (account.UnmatchedReason) reasons.add(account.UnmatchedReason)
      if (account.SellingEntity) entities.add(account.SellingEntity)
      if (account.LegalCountry) countries.add(account.LegalCountry)
      if (account.LanguageCode) languages.add(account.LanguageCode)
    }
    const toOptions = (values: Set<string>) =>
      [...values].sort().map((value) => ({ label: value, value }))
    return {
      reasonOptions: toOptions(reasons),
      sellingEntityOptions: toOptions(entities),
      countryOptions: toOptions(countries),
      languageOptions: toOptions(languages),
    }
  }, [accounts])

  const columns = useMemo(
    () => createLicenseAccountColumns(derivedOptions),
    [derivedOptions],
  )

  return (
    <Table
      className={cn("license-account-table", className)}
      tableId="license-account-table"
      data={rows}
      columns={columns}
      enableGrouping
      {...tableProps}
    >
      <Table.Toolbar>
        {/* Namespaced: three boards share the Licensing page, so the default
            "status" param would leak pill state between tabs. */}
        <Table.StatusFilter
          column="LinkStatus"
          urlParam="accounts-status"
          isLoading={loading}
        >
          <Table.StatusFilter.Option value={null} count={rows.length}>
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={0}
            filterValue={UNMATCHED_STATUS}
            variant="destructive"
            iconSrc="exclamation"
            count={unmatchedCount}
          >
            Unmatched
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={1}
            filterValue={ACKNOWLEDGED_STATUS}
            variant="warning"
            count={acknowledgedCount}
          >
            Acknowledged
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={2}
            filterValue={LINKED_STATUS}
            variant="success"
            iconSrc="check-circle"
            count={linkedCount}
          >
            Linked
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={3}
            filterValue={ARCHIVED_STATUS}
            count={
              rows.length - unmatchedCount - acknowledgedCount - linkedCount
            }
          >
            Archived
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />
        <Table.Search
          column="CompanyName"
          placeholder="Search by account name..."
        />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={() => void refetch()}
          exportFileName="billing-accounts"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<LicenseAccountRow> column="CompanyName">
        {(account) => <LicenseAccountTableRowToolbar account={account} />}
      </Table.RowToolbar>

      <Table.Content isLoading={loading} error={error} />
    </Table>
  )
}

export default LicenseAccountTable
